export function browserDownloadFile(file: File, name?: string) {
  const url = URL.createObjectURL(file);
  const domA = document.createElement('a');
  domA.download = name ?? file.name;
  domA.href = url;
  domA.click();
}

export type GenerateThumbnailResult = {
  originalWidth: number;
  originalHeight: number;
  width: number;
  height: number;
  getFile: () => Promise<File>;
  getDataUrl: () => string;
};

export async function generateThumbnail(
  file: File,
  options: {
    maxWidth?: number;
    maxHeight?: number;
    quality?: number;
    genFile?: boolean;
  } = {}
) {
  const { maxWidth = 200, maxHeight = 200, quality = 0.5 } = options;

  function calculateAspectRatioFit(
    srcWidth: number,
    srcHeight: number,
    maxWidth: number,
    maxHeight: number
  ): { width: number; height: number } {
    const ratio = Math.min(maxWidth / srcWidth, maxHeight / srcHeight);
    return {
      width: srcWidth * ratio,
      height: srcHeight * ratio,
    };
  }

  return new Promise<GenerateThumbnailResult>((resolve, reject) => {
    if (!file.type.startsWith('image/')) {
      reject(new Error('File type is not image.'));
      return;
    }

    const imgUrl = URL.createObjectURL(file);
    const img = new window.Image();

    img.onload = async () => {
      URL.revokeObjectURL(imgUrl);

      try {
        const { width, height } = calculateAspectRatioFit(
          img.width,
          img.height,
          maxWidth,
          maxHeight
        );

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d')!;

        ctx.drawImage(img, 0, 0, width, height);

        resolve({
          getDataUrl: () => canvas.toDataURL(file.type, quality),
          getFile: () =>
            new Promise((resolve) => {
              canvas.toBlob((blob) => {
                resolve(new File([blob!], file.name));
              }, file.type);
            }),
          originalWidth: img.width,
          originalHeight: img.height,
          width,
          height,
        });
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(imgUrl);
      reject(new Error('Image load error.'));
    };

    img.src = imgUrl;
  });
}

export async function downloadImage(url: string, customName?: string) {
  try {
    if (!url) {
      return Promise.reject(new Error('url is empty'));
    }
    const defaultName = url.split('/').pop()?.split('?')[0];
    const filename = customName || defaultName || Date.now().toString();
    const response = await fetch(url);
    if (!response.ok) {
      return Promise.reject(response.statusText);
    }
    const blob = await response.blob();
    let mimeType = blob.type;
    if (!mimeType || mimeType === 'application/octet-stream') {
      if (url.toLowerCase().endsWith('.svg')) {
        mimeType = 'image/svg+xml';
      } else if (url.toLowerCase().endsWith('.png')) {
        mimeType = 'image/png';
      } else if (url.toLowerCase().endsWith('.jpg') || url.toLowerCase().endsWith('.jpeg')) {
        mimeType = 'image/jpeg';
      } else if (url.toLowerCase().endsWith('.gif')) {
        mimeType = 'image/gif';
      } else if (url.toLowerCase().endsWith('.webp')) {
        mimeType = 'image/webp';
      }
    }

    const blobUrl = URL.createObjectURL(new Blob([blob], { type: mimeType }));
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      document.body.removeChild(link);
      URL.revokeObjectURL(blobUrl);
    }, 100);
    return Promise.resolve();
  } catch (error) {
    return Promise.reject(error);
  }
}
